import { useEffect, useRef, useState } from 'react';

import { RadioButtonComponent } from '@syncfusion/ej2-react-buttons';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { TreeGridComponent, Inject as InjectToolbar, Toolbar, ExcelExport } from '@syncfusion/ej2-react-treegrid';
import moment from 'moment';
import { Row } from 'react-bootstrap';

import { formatUTCDate } from 'common/widgets';
import { requests } from 'components/AvaEzm/AxiosApi';
import { CapRatesColumns } from 'components/ProviderData/IpaAdministration/atoms/IpaAdministration.Metadata';
import { CapRateDetail, IPADates, IPADetail } from 'components/ProviderData/IpaAdministration/atoms/Models';
import { CapRatesForm } from 'components/ProviderData/IpaAdministration/organisms/CapRates/CapRatesForm';
import { CapSpecialRateForm } from 'components/ProviderData/IpaAdministration/organisms/CapRates/CapSpecialRateForm';
import { CapTieredRates } from 'components/ProviderData/IpaAdministration/organisms/CapRates/CapTieredRates';
import { submitCapRateService } from 'components/ProviderData/subjects/IpaAdministrationService';
import { dialogService } from 'subjects/common/DialogService';

import 'components/ProviderData/IpaAdministration/organisms/CapRates/CapRates.scss';

type CapRatesProps = {
    ipaCode: string;
    ipaDates: IPADates;
    ipaId?: number;
    ipa: IPADetail;
    isReadOnlyViewer: boolean;
};
interface CapRateData {
    id?: number;
    sequence?: number;
    ipaCode?: string;
    isCapRate: boolean;
    isSpecialRate: boolean;
    companyCode: string;
}

const CapRatesTab = (props: CapRatesProps) => {
    const { ipaCode, ipaDates, ipaId, ipa, isReadOnlyViewer } = props;
    const { companyCode, ipaPhone } = ipa;
    const { effDate, endDate } = ipaDates;
    const [dataSource, setDataSource] = useState<CapRateDetail[]>([]);
    const toastRef = useRef<ToastComponent>(null);
    const capRateGridRef= useRef<TreeGridComponent>(null);
    const loaderRef = useRef<HTMLDivElement>(null);
    const [capRate, setCapRate] = useState<CapRateData>({ isCapRate: true } as CapRateData);
    const [maxEndDate, setMaxEndDate] = useState<Date>();
    const activeIPACodeOrIdFilter = useRef("code");
    const [prevDatesCap, setPrevDatesCap] = useState<
        | {
              effDate: Date | string;
              endDate: Date | string;
          }[]
        | null
    >(null);
    const formatData = (d) => ({
        ...d,
        effDate: formatUTCDate(d.effDate),
        endDate: formatUTCDate(d.endDate),
        createDate: formatUTCDate(d.createDate || d.createdDate),
        updateDt: formatUTCDate(d.updateDt || d.updatedDate),
        sequence: d.memberCountStart ? `Range (${d.memberCountStart} - ${d.memberCountEnd})` : d.sequence,
        sequenceNumber: d.sequence,
    });
    const fetchCapRateDetail = async (_id) => {
        const capRateDetail = await requests.get<CapRateDetail>(`/api/CapRate/GetCapRateDetail?id=${_id}`);
        return capRateDetail;
    };
    const updateDataSource = async () => {
        const codePath = `GetCapRates?IpaCode=${ipaCode}`;
        const idPath = `GetCapRatesByIpaId?IpaId=${ipaId}`;
        const searchUrlCapRates = `/api/CapRate/${activeIPACodeOrIdFilter.current === 'code' ? codePath : idPath }`;

        const searchUrlCapSubRates = `/api/CapRate/GetCapSubRates?IpaCode=${ipaCode}`;
        showLoader(true);
        await requests
            .get<any>(searchUrlCapRates)
            .then(async (data) => {
                for (const _capRate of data) {
                    const detail = await fetchCapRateDetail(_capRate.id);
                    _capRate.detail = detail;
                }
                const capSubRates = await requests.get<any>(searchUrlCapSubRates);
                const formattedData = data.map(formatData).map((d) => ({
                    ...d,
                    capSubRates: capSubRates
                        .filter((t) => t.capId === d.id)
                        .map(formatData)
                        .sort((a, b) => a.capSubRateType - b.capSubRateType),
                }));
                setDataSource(formattedData);
                if (!formattedData?.find((d: CapRateDetail) => d.id === capRate.id)) setCapRate({} as CapRateData);

                const maxEffEndDate = new Date(
                    Math.max(
                        ...data.map((d) => {
                            return new Date(d.endDate);
                        }),
                    ),
                );
                setMaxEndDate(maxEffEndDate);
            })
            .catch((error) => console.log(error))
            .finally(() => showLoader(false));
    };

    const showLoader = (show: boolean) => {
        if (loaderRef.current) loaderRef.current.style.display = show ? 'block' : 'none';
    };

    useEffect(() => {
        updateDataSource();
        if (capRateGridRef && isReadOnlyViewer) {
            // Disable toolbar items.
            capRateGridRef.current?.toolbarModule.enableItems(
                [capRateGridRef.current.element.id + '_gridcontrol_Create'],
                false,
            );
        }
    }, [companyCode]);

    useEffect(() => {
        const service = submitCapRateService.get$().subscribe(({ type, capRateData }) => {
            if (type === 'submit' || type === 'deactivate' || type === 'activate') {
                const typesText = {
                    submit: 'Saved',
                    activate: 'Activated',
                    deactivate: 'Deactivated',
                };
                toastRef.current?.show({
                    title: 'Success !',
                    content: `Cap Rate ${typesText[type]} Successfully`,
                    cssClass: 'e-toast-success',
                });
                updateDataSource();
                setCapRate({} as CapRateData);
            } else if (type === 'cancel') {
                setCapRate({} as CapRateData);
                dialogService.close();
            } else if (type === 'deleteCapSpecialRate') {
                setCapRate({} as CapRateData);
                toastRef.current?.show({
                    title: 'Delete !',
                    content: `Cap Special Rate was deleted Successfully`,
                    cssClass: 'e-toast-success',
                });
                updateDataSource();
                dialogService.close();
            } else if (type === 'addCapTier' || type === 'addCapSpecialRate') {
                const _capRateData = {
                    ...capRateData,
                    sequence: 0,
                    isCopy: false,
                    companyCode: capRateData.companyCode,
                } as CapRateData;
                setCapRate(_capRateData);
                showCreateDialog(_capRateData, true);
            } else if (type === 'copyCapRate') {
                const _capRateData = {
                    ...capRateData,
                    sequence: 0,
                    isCopy: true,
                } as CapRateData;
                setCapRate(_capRateData);
                showCreateDialog(_capRateData, false);
            }
        });
        return () => {
            service.unsubscribe();
        };
    }, [dataSource]);

    const setPreviousCapDates = (row) => {
        const previousCapRate = dataSource
            .sort((a, b) => moment(b.endDate).unix() - moment(a.endDate).unix())
            .filter((d) => d.id !== row.id);
        let extractDates = {} as CapRateDetail[];
        if (row.parentItem) {
            extractDates = [row.parentItem];
        } else if (previousCapRate) {
            extractDates = previousCapRate;
        }
        setPrevDatesCap(extractDates);
        return extractDates;
    };

    const setCapRateDetailRow = (row: any) => {
        setCapRate({
            id: row.id ? row.id : row.capId,
            sequence: row.sequenceNumber,
            isCapRate: row.capSubRates == undefined ? false : true,
            isSpecialRate: row.capSubRateType == 'Special Rates' ? true : false,
            effDate: row.effDate,
            endDate: row.endDate,
            companyCode: row.companyCode,
            ...row.detail,
        } as CapRateData);
        setPreviousCapDates(row);
    };

    const FormCapRates = ({ capRate, prevDatesCap, isNew = false }) => {
        return capRate.isCapRate ? (
            <CapRatesForm
                sequence={isNew ? 0 : capRate?.sequence}
                ipaCode={ipaCode}
                ipaId={ipa?.id}
                id={capRate?.id}
                capRate={capRate}
                ipaInfo={{ dates: { effDate, endDate }, ipaPhone }}
                prevDatesCap={prevDatesCap}
                maxEndDate={maxEndDate}
                isReadOnlyViewer={isReadOnlyViewer}
            />
        ) : capRate.isSpecialRate ? (
            <CapSpecialRateForm
                ipaDetails={ipa}
                capRateId={capRate?.id}
                companyCode={capRate?.companyCode ?? prevDatesCap[0].companyCode}
                capSpecialRateId={capRate?.addNew ? 0 : capRate.id}
                dates={{ effDate: capRate.effDate, endDate: capRate.endDate }}
                isReadOnlyViewer={isReadOnlyViewer}
            />
        ) : (
            <CapTieredRates
                isNew={isNew}
                sequence={isNew ? 0 : capRate?.sequence}
                ipaCode={ipaCode ? ipaCode : capRate.ipaCode}
                capId={capRate?.id}
                dates={{ effDate: capRate.effDate, endDate: capRate.endDate }}
                prevDatesCap={prevDatesCap?.find((d) => d)}
                isReadOnlyViewer={isReadOnlyViewer}
                capTierRate={capRate?.taskData}
            />
        );
    };

    const toolbar = () => {
        const radioButtonGroupDifferentiator = (new Date()).getMilliseconds();

        return [
            'Create',
            {
                align: 'right',
                id: 'filterCapRatesBy',
                tooltipText: 'Filter Cap Rates By',
                template: () => {
                    return <div className='toolbar-filter-by-container'>
                        <span className='id-or-code'>Filter By:</span>
                        &emsp;&emsp;
                        <RadioButtonComponent label={`IPA Code: ${ipaCode}`} name={`filterType_${radioButtonGroupDifferentiator}`} value="code" checked={activeIPACodeOrIdFilter.current === 'code'} onClick={filterByIdOrCode} />
                        &emsp;&emsp;&emsp;&emsp;
                        <RadioButtonComponent label={`IPA ID: ${ipaId}`} name={`filterType_${radioButtonGroupDifferentiator}`} value="id" checked={activeIPACodeOrIdFilter.current === 'id'} onClick={filterByIdOrCode} />
                    </div>;
                },
            },
        ];
    }

    const toolbarClick = ({ item }) => {
        if (item.text === 'Create') {
            submitCapRateService.add();
            setCapRate({ sequence: 0, isCapRate: true } as CapRateData);
            showCreateDialog({ sequence: 0, isCapRate: true } as CapRateData, false);
        }
    };

    const filterByIdOrCode = (args) => {
        if (args.target.value !== activeIPACodeOrIdFilter.current) {
            activeIPACodeOrIdFilter.current = args.target.value;
            updateDataSource();
        }
    }

    const showCreateDialog = (_capRate, isCapRateCreation) => {
        let _prevDatesCap = setPreviousCapDates({
            sequenceNumber: Infinity,
            isCapRate: _capRate.isCapRate,
        });
        if (isCapRateCreation) {
            const { effDate, endDate } = _capRate;
            _prevDatesCap = [{ effDate, endDate }] as CapRateDetail[];
        }
        const dialog: any = {};
        if (_capRate.isCapRate) {
            dialog.header = _capRate.isCopy ? 'Copy Cap Rate' : 'Create Cap Rate';
        } else {
            if (_capRate.isSpecialRate) {
                dialog.header = 'Create Cap Special Rate';
            } else {
                dialog.header = 'Create Cap Tiered Rate';
            }
        }
        dialog.buttons = [];
        dialog.content = () => (
            <FormCapRates
                isNew
                capRate={_capRate}
                prevDatesCap={_prevDatesCap}
            />
        );
        dialog.width = 1000;
        dialogService.open(dialog);
    };

    return (
        <div className='caprates-grid-parent tab-content tabDiv p-3'>
            <Row>
                <Row>
                    <div
                        ref={loaderRef}
                        style={{ position: 'sticky', top: '57vh', height: '0px', zIndex: '999999', display: 'none' }}
                    >
                        <div
                            style={{
                                height: '100%',
                                width: '100%',
                                position: 'fixed',
                                top: '20px',
                                zIndex: '99990',
                                backdropFilter: 'blur(2px)',
                            }}
                        ></div>
                        <div
                            className='spinner-border'
                            style={{
                                position: 'sticky',
                                top: '200px',
                                left: 'calc(65% - 20px)',
                                height: '50px',
                                width: '50px',
                                color: 'rgb(227, 22, 91)',
                                zIndex: '999999',
                            }}
                        ></div>
                    </div>
                    <TreeGridComponent
                        dataSource={dataSource}
                        columns={CapRatesColumns as any[]}
                        treeColumnIndex={0}
                        height='350'
                        childMapping='capSubRates'
                        rowSelected={({ data }) => setCapRateDetailRow(data)}
                        selectionSettings={{ mode: 'Row', type: 'Single' }}
                        ref={capRateGridRef}
                        toolbar={toolbar() as any}
                        editSettings={{ allowAdding: true }}
                        toolbarClick={toolbarClick}
                        allowSelection={true}
                        allowExcelExport={true}
                        showColumnChooser={true}
                    >
                        <InjectToolbar services={[Toolbar, ExcelExport]} />
                    </TreeGridComponent>
                </Row>
                <Row style={{ padding: '20px' }}>
                    {capRate?.sequence !== undefined && capRate?.sequence !== 0 ? (
                        <FormCapRates
                            capRate={capRate}
                            prevDatesCap={prevDatesCap}
                        />
                    ) : null}
                </Row>
            </Row>
            <ToastComponent
                id='toast_target'
                ref={toastRef}
                title=''
                content=''
                position={{ X: 'Right', Y: 'Top' }}
            />
        </div>
    );
};

export default CapRatesTab;
